// askSmart-ui/src/pages/MapleCoin/operations/shared.tsx
//
// Small pieces shared by the Operations tabs. Money is formatted from exact
// minor-unit strings only (see ../money.ts).
import { ReactNode, createContext, useContext } from 'react';
import { Loader2 } from 'lucide-react';
import { formatMoney } from '../money';
import { Currency, MinorString } from '../types';

export const dateTime = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString('en-CA', { dateStyle: 'medium', timeStyle: 'medium' }) : '—';

export const titleCase = (s: string) => s.charAt(0) + s.slice(1).toLowerCase().replace(/_/g, ' ');

/** "1,234.56" → "123456", by string manipulation only. null when not an amount. */
export function parseDecimalToMinor(text: string): string | null {
  const t = text.trim().replace(/,/g, '');
  if (!/^[0-9]{1,16}(\.[0-9]{0,2})?$/.test(t)) return null;
  const [whole, fraction = ''] = t.split('.');
  return (whole + fraction.padEnd(2, '0')).replace(/^0+(?=[0-9])/, '');
}

/** Local calendar day → the ISO instant it starts (for "from"), or the next day's start (for an inclusive "to"). */
export function dayStart(day: string, nextDay = false): string | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return undefined;
  const d = new Date(`${day}T00:00:00`);
  if (nextDay) d.setDate(d.getDate() + 1);
  return d.toISOString();
}

export function Amount({ minor, currency, testId }: { minor: MinorString | null; currency: Currency; testId?: string }) {
  return <span className="mco-num" data-testid={testId}>{minor === null ? '—' : formatMoney(minor, currency)}</span>;
}

const STATUS_TONE: Record<string, string> = {
  COMPLETED: 'ok', EXECUTED: 'ok', VERIFIED: 'ok', OPEN: 'ok',
  PENDING: 'wait', PENDING_APPROVAL: 'wait', CREATED: 'wait', FROZEN: 'wait',
  FAILED: 'bad', REJECTED: 'muted', EXECUTION_REFUSED: 'bad', PROBLEMS_FOUND: 'bad', CLOSED: 'muted',
  REVERSED: 'muted',
  ACTIVE: 'wait', RELEASED: 'muted', EXPIRED: 'muted',
  RUNNING: 'wait', SUCCEEDED: 'ok', ABANDONED: 'bad',
};

export function Status({ value, label }: { value: string; label?: string }) {
  return <span className={`mco-status mco-status--${STATUS_TONE[value] ?? 'muted'}`}>{label ?? titleCase(value)}</span>;
}

export interface Person { name: string | null; email: string | null }

/**
 * Names and emails for the Auth0 ids in a view, as resolved by the server.
 * Display only: the id remains the identity, shown on hover.
 */
export const PeopleContext = createContext<Record<string, Person>>({});

export function Identity({ sub, you }: { sub: string; you?: boolean }) {
  const person = useContext(PeopleContext)[sub];
  const label = person?.name ?? person?.email;
  return (
    <span className="mco-identity" title={sub}>
      {label
        ? <span className="mco-person">{label}{person?.name && person?.email && <span className="mco-dim"> · {person.email}</span>}</span>
        : <code>{sub}</code>}
      {you && <span className="mco-you">you</span>}
    </span>
  );
}

export function Unavailable({ onRetry }: { onRetry?: () => void }) {
  return (
    <div className="mco-panel mco-state" role="alert">
      <p className="mco-state-title">MapleCoin Operations is temporarily unavailable.</p>
      {onRetry && <button type="button" className="mco-btn" onClick={onRetry}>Try again</button>}
    </div>
  );
}

export function Loading({ label = 'Loading' }: { label?: string }) {
  return <div className="mco-loading" role="status"><Loader2 size={16} className="mco-spin" /> {label}…</div>;
}

export function Busy({ busy, children }: { busy: boolean; children: ReactNode }) {
  return <>{busy && <Loader2 size={14} className="mco-spin" aria-hidden />}{children}</>;
}

export function KeyValues({ rows }: { rows: [string, ReactNode][] }) {
  return (
    <dl className="mco-kv">
      {rows.map(([k, v]) => (
        <div key={k}><dt>{k}</dt><dd>{v ?? '—'}</dd></div>
      ))}
    </dl>
  );
}

export function Panel({ title, actions, children, label }: { title?: string; actions?: ReactNode; children: ReactNode; label?: string }) {
  return (
    <section className="mco-panel" aria-label={label ?? title}>
      {(title || actions) && (
        <header className="mco-panel-head">
          {title && <h2>{title}</h2>}
          {actions && <div className="mco-panel-actions">{actions}</div>}
        </header>
      )}
      {children}
    </section>
  );
}

/** Debit/credit lines of one journal, with exact amounts and totals. */
export function Entries({ entries }: { entries: { accountCode: string; accountName: string; side: 'DEBIT' | 'CREDIT'; amountMinor: string; currency: Currency }[] }) {
  const total = (side: 'DEBIT' | 'CREDIT') =>
    entries.filter((e) => e.side === side).reduce((a, e) => a + BigInt(e.amountMinor), BigInt(0)).toString();
  const currency = entries[0]?.currency ?? 'CAD';
  return (
    <div className="mco-table-wrap">
      <table className="mco-table mco-entries">
        <thead><tr><th>Account</th><th>Name</th><th className="mco-r">Debit</th><th className="mco-r">Credit</th></tr></thead>
        <tbody>
          {entries.map((e, i) => (
            <tr key={i}>
              <td><code>{e.accountCode}</code></td>
              <td>{e.accountName}</td>
              <td className="mco-r">{e.side === 'DEBIT' ? <Amount minor={e.amountMinor} currency={e.currency} /> : ''}</td>
              <td className="mco-r">{e.side === 'CREDIT' ? <Amount minor={e.amountMinor} currency={e.currency} /> : ''}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr><td colSpan={2}>Totals</td>
            <td className="mco-r"><Amount minor={total('DEBIT')} currency={currency} /></td>
            <td className="mco-r"><Amount minor={total('CREDIT')} currency={currency} /></td></tr>
        </tfoot>
      </table>
    </div>
  );
}
